import React, { useMemo, useState } from 'react';
import { Modal, Button, Dropdown, DropdownButton, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import { useFleetAlerts } from '../hooks/useMeters';
import { toCsv } from '../utils/waterExport';
import { downloadCsv } from '../utils/downloadCsv';
import {
  REPORT_PERIODS,
  buildDowntimeReport,
  describeDowntime,
  coverageNote,
  toDowntimeRows,
  filterDowntime,
  metersInReport,
  faultsInReport,
  isFiltered,
  NO_FILTERS,
} from '../utils/downtimeReport';

/**
 * Which meters were down over a period, for how long, and why.
 *
 * The strip above the meter grid answers "is anything wrong now". This answers
 * "what has this fleet been doing", which is the question asked when a monthly
 * total looks low or a report is due — and until now it could only be answered
 * one meter at a time from the details page.
 *
 * It reads the alert instances the status job already raises for the scheduled
 * email, so it costs one request and stores nothing new.
 *
 * @param {boolean} show - Whether the modal is open.
 * @param {Function} onHide - Closes it.
 * @param {Array<object>} meters - Meters, for names and when each last spoke.
 */
const DowntimeReportModal = ({ show, onHide, meters = [] }) => {
  const [period, setPeriod] = useState('month');
  const [filters, setFilters] = useState(NO_FILTERS);
  const { data, isLoading, error } = useFleetAlerts(show);

  const instances = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const report = useMemo(
    () => buildDowntimeReport(instances, meters, period),
    [instances, meters, period]
  );

  const caveat = useMemo(
    () => coverageNote(meters, instances),
    [meters, instances]
  );

  /** Offered from what the period holds, so no choice leads to an empty table. */
  const meterChoices = useMemo(() => metersInReport(report), [report]);
  const faultChoices = useMemo(() => faultsInReport(report), [report]);

  const shown = useMemo(() => filterDowntime(report, filters), [report, filters]);
  const narrowed = isFiltered(filters);

  /**
   * Changing the period changes which meters and faults exist, so a filter
   * chosen for the last one is cleared rather than left to select nothing.
   */
  const choosePeriod = (id) => {
    setPeriod(id);
    setFilters(NO_FILTERS);
  };

  const choose = (field) => (event) =>
    setFilters((current) => ({ ...current, [field]: event.target.value }));

  const toggleOpenOnly = (event) =>
    setFilters((current) => ({ ...current, openOnly: event.target.checked }));

  /**
   * Downloads what is asked for, named for what it holds.
   *
   * A file called meter-downtime-month.csv holding four of forty outages is
   * indistinguishable from one holding a quiet month once it is off the page,
   * so a narrowed export says so in its name.
   *
   * @param {boolean} onlyShown - Whether to export the filtered rows.
   */
  const exportRows = (onlyShown) =>
    downloadCsv(
      `meter-downtime-${period}${onlyShown ? '-filtered' : ''}.csv`,
      toCsv(toDowntimeRows(onlyShown ? shown : report))
    );

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title as="h5">Downtime report</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="im-dr-controls">
          <div className="im-dr-periods" role="group" aria-label="Report period">
            {REPORT_PERIODS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                className={`im-dr-period${id === period ? ' im-dr-period--on' : ''}`}
                aria-pressed={id === period}
                onClick={() => choosePeriod(id)}
              >
                {label}
              </button>
            ))}
          </div>

          {narrowed ? (
            <DropdownButton
              variant="outline-primary"
              size="sm"
              align="end"
              disabled={!report.outages}
              title={
                <>
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Export
                </>
              }
            >
              <Dropdown.Item onClick={() => exportRows(true)}>
                These {shown.outages} outages
              </Dropdown.Item>
              <Dropdown.Item onClick={() => exportRows(false)}>
                All {report.outages} in this period
              </Dropdown.Item>
            </DropdownButton>
          ) : (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() => exportRows(false)}
              disabled={!report.outages}
            >
              <FontAwesomeIcon icon={faDownload} className="me-2" />
              Export
            </Button>
          )}
        </div>

        {!isLoading && !error && report.outages > 0 && (
          <div className="im-dr-filters">
            <Form.Group controlId="im-dr-meter">
              <Form.Label className="im-dr-filter-label">Meter</Form.Label>
              <Form.Select size="sm" value={filters.meterId} onChange={choose('meterId')}>
                <option value="">All meters</option>
                {meterChoices.map(({ id, name }) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group controlId="im-dr-fault">
              <Form.Label className="im-dr-filter-label">Fault</Form.Label>
              <Form.Select size="sm" value={filters.fault} onChange={choose('fault')}>
                <option value="">All faults</option>
                {faultChoices.map((fault) => (
                  <option key={fault} value={fault}>{fault}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Check
              type="checkbox"
              id="im-dr-open"
              className="im-dr-open"
              label="Still down"
              checked={filters.openOnly}
              onChange={toggleOpenOnly}
            />

            {narrowed && (
              <Button variant="link" size="sm" onClick={() => setFilters(NO_FILTERS)}>
                Clear
              </Button>
            )}
          </div>
        )}

        {isLoading && (
          <div className="im-dr-quiet">
            <Spinner animation="border" size="sm" variant="primary" />
          </div>
        )}

        {!isLoading && error && (
          <p className="im-dr-quiet im-dr-quiet--bad">
            The alert history could not be loaded, so nothing here is a record
            of what happened.
          </p>
        )}

        {!isLoading && !error && (
          <>
            <p className="im-dr-summary">{describeDowntime(shown, period)}</p>

            {report.outages > 0 && shown.outages === 0 && (
              <p className="im-dr-quiet">
                No outage in this period matches that. Widen the filters or
                clear them to see the {report.outages} there are.
              </p>
            )}

            {shown.outages > 0 && (
              <table className="im-dr-table">
                <thead>
                  <tr>
                    <th>Meter</th>
                    <th>Started</th>
                    <th>Ended</th>
                    <th>Down for</th>
                    <th>Fault</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.rows.map((row) => (
                    <tr key={row.id} className={row.open ? 'im-dr-row--open' : ''}>
                      <td>{row.meterName}</td>
                      <td>{row.started}</td>
                      <td>{row.open ? 'Still down' : row.ended}</td>
                      <td>{row.length}</td>
                      <td>{row.fault}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {caveat && <p className="im-dr-caveat">{caveat}</p>}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default DowntimeReportModal;
